import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Icon, ICONS } from '@/lib/icons'
import { useState } from 'react'

// Single source of truth for app version
const APP_VERSION = '2.4.0'

export function UserManualSV() {
  const [changelogOpen, setChangelogOpen] = useState(false)
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Användarmanual</h1>
        <p className="text-lg text-muted-foreground">Komplett guide för Klubbspel turneringshantering</p>
      </div>

      {/* Introduction */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.info} className="text-info" />
            Välkommen till Klubbspel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-lg">
            Klubbspel är ett kostnadsfritt turneringssystem för alla som vill organisera serier och turneringar inom racket- och brädspel.
          </p>
          <p>
            Perfekt för idrottsklubbar, kompisgäng, företag med rastaktiviteter, familjer eller vem som helst som vill hålla koll på matcher och ranking.
            Systemet hanterar turneringsserier, matchrapportering, ELO-baserad ranking och automatisk poängberäkning – enkelt att använda för alla!
          </p>
          <div className="bg-info-muted border border-info/30 rounded-lg p-4 mt-4 text-info-muted-foreground">
            <p className="font-semibold mb-2">🚀 Snabbstart</p>
            <ol className="list-decimal list-inside space-y-1">
              <li><a href="/login" className="underline hover:text-info">Logga in</a> med din e-post (lösenordsfritt)</li>
              <li>Skapa eller gå med i en klubb</li>
              <li>Starta en turneringsserie</li>
              <li>Bjud in spelare och rapportera matcher</li>
            </ol>
          </div>
          <p className="font-semibold text-foreground">
            Sporter som stöds (exempel - fler kommer):
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.tableTennis} className="text-orange-600" />
              <span>Bordtennis</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.tennis} className="text-green-600" />
              <span>Tennis</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.badminton} className="text-info" />
              <span>Badminton</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.padel} className="text-purple-600" />
              <span>Padel</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.squash} className="text-yellow-600" />
              <span>Squash</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.pickleball} className="text-pink-600" />
              <span>Pickleball</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.chess} className="text-muted-foreground" />
              <span>Schack</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.beachTennis} className="text-cyan-600" />
              <span>Beach Tennis</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.tableHockey} className="text-red-600" />
              <span>Bordshockey</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-muted rounded">
              <Icon icon={ICONS.foosball} className="text-amber-700" />
              <span>Fotbollsspel</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.user} className="text-info" />
            Komma igång
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">1. Logga in</h3>
            <p className="text-muted-foreground mb-3">
              Klicka på "Logga in" i det övre högra hörnet. Ange din e-postadress så skickar vi en magisk länk för att bekräfta din identitet.
              Ingen komplicerad lösenordshantering - bara klicka på länken i e-postmeddelandet.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-lg mb-2">2. Gå med i eller skapa en klubb</h3>
            <p className="text-muted-foreground mb-3">
              Gå till fliken "Klubbar" och antingen gå med i en befintlig klubb eller skapa en ny. För att skapa matcher och turneringar
              måste du tillhöra en klubb.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">3. Komplettera din profil</h3>
            <p className="text-muted-foreground mb-3">
              Innan du kan delta i tävlingar måste du lägga till ditt för- och efternamn i inställningar. Detta säkerställer att
              andra spelare kan identifiera dig i resultatlistorna.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Permissions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.shield} className="text-info" />
            Behörigheter och roller
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground mb-3">
            Klubbspel använder ett trestegssystem för behörigheter:
          </p>

          <div className="space-y-4">
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-lg mb-1">Plattformsägare</h3>
              <p className="text-muted-foreground">
                Har fullständig kontroll över alla klubbar, serier och matcher. Kan göra administrativa åtgärder överallt i systemet.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg mb-1">Klubbadministratör</h3>
              <p className="text-muted-foreground mb-2">
                Hanterar sin klubbs turneringar och medlemmar. Kan:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
                <li>Skapa och hantera turneringsserier</li>
                <li>Redigera eller ta bort alla matcher i klubbens serier</li>
                <li>Generera slutspelets bracket</li>
                <li>Avsluta turneringar och låsa resultat</li>
                <li>Lägga till spelare till klubben</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-lg mb-1">Spelare</h3>
              <p className="text-muted-foreground mb-2">
                Kan hantera sina egna matcher. Kan:
              </p>
              <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
                <li>Rapportera resultat för matcher där de deltar</li>
                <li>Redigera sina egna matcher (innan de låses)</li>
                <li>Visa alla turneringar och resultatlistor</li>
                <li>Exportera matchdata till CSV</li>
              </ul>
            </div>
          </div>

          <div className="bg-warning-muted border border-warning rounded p-4 mt-4">
            <p className="text-sm text-muted-foreground">
              <strong>Obs:</strong> När en match har spelats och en senare match i samma turneringsträd är rapporterad,
              låses den första matchen automatiskt. Detta förhindrar att resultat ändras på ett sätt som skulle påverka redan
              spelade matcher. Endast administratörer kan låsa upp låsta matcher.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tournament Formats */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.trophy} className="text-info" />
            Turneringsformat
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.tournament} className="text-orange-600" />
              Öppen serie (Round-robin)
            </h3>
            <p className="text-muted-foreground mb-2">
              Alla spelar mot alla. Perfekt för säsongsligor där alla får möjlighet att möta varandra.
              Rankas med ELO-poäng eller vinstprocent.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.chart} className="text-green-600" />
              Stegturneringar (Ladder)
            </h3>
            <p className="text-muted-foreground mb-3">
              Spelare rangordnas i stegpositioner. Du kan utmana vilken spelare som helst, men det är mest fördelaktigt att utmana uppåt.
            </p>
            
            <div className="bg-muted p-4 rounded space-y-4">
              <div>
                <p className="font-semibold mb-2">När du utmanar uppåt och vinner:</p>
                <p className="text-sm text-muted-foreground mb-2">
                  Du hoppar till den utmanades position och alla spelare däremellan trycks ner ett steg.
                </p>
                <div className="bg-info-muted p-2 rounded text-sm text-info-muted-foreground">
                  <p className="font-medium">Exempel: Du är #5 och slår #2</p>
                  <p>→ Du blir #2, tidigare #2 blir #3, tidigare #3 blir #4, tidigare #4 blir #5</p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-2">När du utmanar uppåt och förlorar:</p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li><strong>Klassisk variant:</strong> Ingen förändring - du behåller din position (ingen straff)</li>
                  <li><strong>Aggressiv variant:</strong> Du åker ner ett steg - byter plats med spelaren under dig (straff för misslyckad utmaning)</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-2">När du utmanar nedåt:</p>
                <p className="text-sm text-muted-foreground mb-1">
                  Om en högre rankad spelare utmanar någon lägre rankad:
                </p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li><strong>Om högre rankad vinner:</strong> Ingen förändring i positioner (förväntat resultat)</li>
                  <li><strong>Om högre rankad förlorar:</strong> Samma regler som "utmana uppåt och vinna" tillämpas - vinnaren hoppar upp</li>
                </ul>
              </div>

              <div className="bg-warning-muted border border-warning rounded p-3 mt-3">
                <p className="text-sm font-semibold text-foreground mb-1">💡 Strategi:</p>
                <p className="text-sm text-muted-foreground">
                  Det är mest fördelaktigt att utmana spelare högre upp på stegen. Utmanar du nedåt riskerar du att förlora din position utan möjlighet till vinst.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.trophy} className="text-purple-600" />
              Cupformat (Slutspel)
            </h3>
            <p className="text-muted-foreground mb-3">
              Singeleliminering - förlora och du är ute. Stöder 4, 8, 16 eller 32 spelare. Vinnare går vidare till nästa rond
              tills endast en mästare återstår.
            </p>
            <div className="bg-info-muted border border-info/30 rounded p-3 text-info-muted-foreground">
              <p className="text-sm">
                <strong>Bronsmatch:</strong> Valfritt kan du aktivera en match om tredjepris mellan förlorarna i semifinalerna.
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Icon icon={ICONS.users} className="text-pink-600" />
              Gruppspel + Slutspel
            </h3>
            <p className="text-muted-foreground mb-3">
              Kombinerar gruppspel (round-robin) med ett efterföljande slutspel. Spelar använder först alla-mot-alla i mindre grupper,
              sedan går de bästa spelarna vidare till slutspelets bracket.
            </p>
            
            <div className="space-y-3">
              <div>
                <p className="font-semibold mb-1">Gruppspel regler:</p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li>Stöder 2-8 grupper med 3-8 spelare per grupp</li>
                  <li>
                    Poängsystem (sportspecifikt):
                    <ul className="list-circle ml-6 mt-1 space-y-1">
                      <li><strong>Racket/Paddle-sporter</strong> (Bordtennis, Tennis, Badminton, Squash, Padel, Pickleball): Vinst = 2p, Oavgjort = 1p, Förlust = 0p</li>
                      <li><strong>Schack</strong>: Vinst = 1.0p, Remi = 0.5p, Förlust = 0.0p (traditionellt FIDE-system)</li>
                      <li><strong>Bollsporter</strong> (framtida: Fotboll, Hockey, Basket): Vinst = 3p, Oavgjort = 1p, Förlust = 0p</li>
                    </ul>
                  </li>
                  <li>För schack: Oavgjort (remi) är tillåtet i gruppspelet</li>
                  <li>Automatisk beräkning av gruppställningar</li>
                  <li>Sortering: Poäng → Sinsemellan → Målskillnad → Antal vinster</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-1">Hantering av udda antal spelare:</p>
                <div className="bg-warning-muted border border-warning rounded p-3 text-sm">
                  <p className="text-muted-foreground mb-2">
                    <strong>Exempel med 3 spelare i en grupp:</strong>
                  </p>
                  <p className="text-muted-foreground">
                    Om gruppen har 3 spelare istället för 4, spelar varje spelare färre matcher (2 matcher istället för 3).
                    Detta påverkar inte poängsystemet - spelaren med flest poäng går vidare. Vid lika många poäng används
                    setskillnad som utslagningsgrund.
                  </p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-1">Avancemang till slutspel:</p>
                <p className="text-sm text-muted-foreground mb-2">
                  När gruppspelet är klart kan administratören generera slutspelets bracket. Systemet tar automatiskt de N bästa
                  spelarna från varje grupp (vanligtvis topp 2) och skapar ett bracket baserat på deras gruppspelsplaceringar.
                </p>
                <div className="bg-info-muted border border-info/30 rounded p-3 text-sm text-info-muted-foreground">
                  <p>
                    <strong>Viktigt:</strong> När slutspelet har genererats låses alla gruppspelsmatcher automatiskt för att
                    förhindra ändringar som skulle påverka playoff-deltagarna.
                  </p>
                </div>
              </div>

              <div>
                <p className="font-semibold mb-1">Slutspelsregler:</p>
                <ul className="list-disc ml-6 space-y-1 text-sm text-muted-foreground">
                  <li>Singeleliminering - en förlust och du är ute</li>
                  <li>För schack: Oavgjort är INTE tillåtet i slutspelet (vinnare måste avgöras)</li>
                  <li>Matcher låses när efterföljande matcher spelas</li>
                  <li>Bracket-storlek bestäms av antalet spelare som går vidare från grupperna</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reporting Matches */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.edit} className="text-info" />
            Rapportera matcher
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">Vem kan rapportera en match?</h3>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li><strong>Deltagande spelare:</strong> Kan rapportera sina egna matcher</li>
              <li><strong>Klubbadministratörer:</strong> Kan rapportera alla matcher i sin klubb</li>
              <li><strong>Plattformsägare:</strong> Kan rapportera vilken match som helst</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Set-baserad rapportering (Racketsporter)</h3>
            <p className="text-muted-foreground mb-3">
              För bordtennis, tennis, badminton, padel, squash, etc. rapporterar du antal vunna set för varje spelare.
              Systemet registrerar INTE individuella poäng inom seten - bara antalet vunna set.
            </p>
            <div className="bg-muted p-3 rounded text-sm space-y-2">
              <p><strong>Exempel (Best-of-5):</strong></p>
              <p>Spelare A: 3 set</p>
              <p>Spelare B: 1 set</p>
              <p className="text-muted-foreground italic">Individuella setresultat (11-9, 8-11, 11-7, 11-5) registreras inte av systemet.</p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Resultat-baserad rapportering (Schack)</h3>
            <p className="text-muted-foreground mb-2">
              För schack väljer du resultat med radioknappar:
            </p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li>Spelare A vinner (1-0)</li>
              <li>Spelare B vinner (0-1)</li>
              <li>Oavgjort (½-½) - endast tillåtet i gruppspel</li>
            </ul>
          </div>

          <div className="bg-warning-muted border border-warning rounded p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Obs:</strong> Alla matcher måste spelas inom seriens start- och slutdatum. System validerar att matchdatumet
              ligger inom detta intervall.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Podium and Celebration */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.podium} className="text-info" />
            Prispall och firande
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            När en turneringsturnering avslutas visar systemet en vacker prispall med OS-stil tre nivåer och färgglad konfetti
            för att fira vinnarna!
          </p>
          
          <div className="bg-gradient-to-br from-warning-muted to-warning-muted border-2 border-warning rounded-lg p-6">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-foreground mb-2">🏆 Turneringsprispall 🎉</h3>
              <p className="text-sm text-muted-foreground italic">
                (Bild visar prispall med konfetti animation när turneringen avslutas)
              </p>
            </div>
            
            <div className="flex items-end justify-center gap-4">
              {/* Silver (2nd place) */}
              <div className="flex flex-col items-center">
                <div className="text-4xl mb-2">🥈</div>
                <div className="bg-gray-300 rounded-t-lg px-6 py-8 text-center">
                  <p className="font-bold text-foreground">2:a plats</p>
                  <p className="text-sm text-muted-foreground">Silver</p>
                </div>
              </div>
              
              {/* Gold (1st place - taller) */}
              <div className="flex flex-col items-center">
                <div className="text-5xl mb-2">🏆</div>
                <div className="bg-yellow-400 rounded-t-lg px-6 py-12 text-center border-2 border-yellow-600">
                  <p className="font-bold text-foreground text-lg">Mästare! 🎉</p>
                  <p className="text-sm text-foreground">Guld</p>
                </div>
              </div>
              
              {/* Bronze (3rd place) */}
              <div className="flex flex-col items-center">
                <div className="text-4xl mb-2">🥉</div>
                <div className="bg-orange-300 rounded-t-lg px-6 py-6 text-center">
                  <p className="font-bold text-foreground">3:e plats</p>
                  <p className="text-sm text-muted-foreground">Brons</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                ✨ Konfetti regnar ned över vinnarna! ✨
              </p>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            <p className="font-semibold">Prispallsfunktioner:</p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li>Olympisk stil med tre nivåer (guld högst, silver och brons vid sidan)</li>
              <li>Konfetti-animering när prispallen visas första gången</li>
              <li>Visar spelarnamn, matcher spelade, vinster och vinstprocent</li>
              <li>Delningsfunktion för att sprida resultaten på sociala medier</li>
              <li>Exportera till CSV för dokumentation</li>
              <li>Tillgänglig för alla turneringsformat när de avslutas</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.userCircle} className="text-info" />
            Avancerade funktioner
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg mb-2">CSV Export</h3>
            <p className="text-muted-foreground">
              Exportera matchdata och rankinglistor till CSV-format för analys i Excel eller andra verktyg.
              Användbar för säsongsstatistik och årlig rapportering.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Slå ihop spelare</h3>
            <p className="text-muted-foreground">
              Om en spelare har registrerats flera gånger (t.ex. med och utan e-post) kan administratörer slå ihop
              spelarprofiler. Detta bevarar all matchhistorik och flyttar all data till en enda profil.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">ELO Ratingsystem</h3>
            <p className="text-muted-foreground mb-2">
              Klubbspel använder standard ELO-beräkning för dynamiska rankings:
            </p>
            <ul className="list-disc ml-6 space-y-1 text-muted-foreground">
              <li>K-faktor: 32 (standard för klubbspel)</li>
              <li>Start-rating: 1500 för nya spelare</li>
              <li>Oavgjorda ger 0.5 poäng till båda spelare (schack)</li>
              <li>Ratings uppdateras i realtid efter varje match</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-2">Sökning och filter</h3>
            <p className="text-muted-foreground">
              Snabb sökning finns tillgänglig för spelare och turneringsserier. Filtrera spelare efter klubb,
              sök efter namn eller e-post, och hitta turneringar efter sport eller status.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tips and Best Practices */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.alertCircle} className="text-info" />
            Tips och bästa praxis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="border-l-4 border-green-500 pl-4">
            <p className="font-semibold mb-1">Rapportera matcher direkt</p>
            <p className="text-sm text-muted-foreground">
              Rapportera matchresultat så snart som möjligt efter matchen för att hålla rankinglistor uppdaterade och
              ge alla spelare aktuell information.
            </p>
          </div>

          <div className="border-l-4 border-blue-500 pl-4">
            <p className="font-semibold mb-1">Kontrollera datum</p>
            <p className="text-sm text-muted-foreground">
              Matchen måste vara spelad inom seriens start- och slutdatum. Kontrollera datumen innan du rapporterar.
            </p>
          </div>

          <div className="border-l-4 border-purple-500 pl-4">
            <p className="font-semibold mb-1">Använd gruppspel + slutspel för stora turneringar</p>
            <p className="text-sm text-muted-foreground">
              För turneringar med många spelare (16+) ger gruppspel alla chansen att spela flera matcher innan
              de bästa går vidare till slutspelet.
            </p>
          </div>

          <div className="border-l-4 border-orange-500 pl-4">
            <p className="font-semibold mb-1">Aktivera bronsmatch för cup-format</p>
            <p className="text-sm text-muted-foreground">
              Att ha en match om tredje pris låter förlorarna i semifinalerna få en extra match och skapar en
              komplett prispall med tre platser.
            </p>
          </div>

          <div className="border-l-4 border-pink-500 pl-4">
            <p className="font-semibold mb-1">Kommunicera regler tydligt</p>
            <p className="text-sm text-muted-foreground">
              Se till att alla deltagare förstår turneringsformatet, om oavgjort är tillåtet (schack gruppspel),
              och hur avancemang till slutspel fungerar.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Version History / Changelog */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.history} className="text-purple-600" />
            Versionshistorik
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Collapsible open={changelogOpen} onOpenChange={setChangelogOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted hover:bg-accent rounded transition-colors">
              <span className="font-medium text-foreground">Visa ändringslogg (v1.0.0 → v2.4.0)</span>
              <Icon 
                icon={changelogOpen ? ICONS.chevronUp : ICONS.chevronDown} 
                className="text-muted-foreground"
                width={20}
                height={20}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                
                {/* v2.4.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.4.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">8 december 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Visuella Teman & Bakgrundsbilder</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Nytt: ThemePicker med 7 bakgrundsalternativ (6 sporter + geometrisk)</li>
                    <li>Bakgrundsbilder för Bordtennis, Tennis, Badminton, Schack, Bordshockey, Padel</li>
                    <li>Optimerade bilder (60-80% mindre) för snabbare laddning</li>
                    <li>Semantiskt färgsystem för bättre konsistens</li>
                    <li>Temaval sparas mellan sessioner</li>
                    <li>Förbättrad visuell anpassning</li>
                  </ul>
                </div>

                {/* v2.3.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.3.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">5 december 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Bordshockey, Fotbollsspel & Noll Säkerhetsproblem</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Nytt: Stöd för bordshockey 🏒 (table hockey) som sport</li>
                    <li>Nytt: Stöd för fotbollsspel 🤾 (foosball) som sport</li>
                    <li>SBHF-kompatibla valideringsregler: 0-50 mål per match</li>
                    <li>Oavgjorda matcher: Tillåtna i grundspel, avvisade i slutspel</li>
                    <li>Sport-medveten API: Dynamisk validering baserad på seriens sporttyp</li>
                    <li>Säkerhet: Uppnådde 0 gosec-problem (ner från 53)</li>
                    <li>Fixat: Routing-problem där icke-inloggade omdirigerades felaktigt</li>
                    <li>Städat: Borttaget alla V2 API-artefakter</li>
                    <li>Testat: 10 API-integrationstester + 15 enhetstester</li>
                  </ul>
                </div>

                {/* v2.2.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.2.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">5 december 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">SEO-förbättringar</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Rik noscript-innehåll för sökmotorindexering (~400 ord)</li>
                    <li>Omdirigering av icke-inloggade besökare till manual-sida</li>
                    <li>Förbättrad användarmanual med snabbstartsguide</li>
                  </ul>
                </div>

                {/* v2.1.5 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.5</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">8 november 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Pagineringsfixar och SEO-förbättringar</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixat: Dubbletter i paginering för spelare, klubbar och serier</li>
                    <li>Implementerat: Generellt keyset-pagineringssystem med sammansatta markörer</li>
                    <li>Tillagt: Metabeskrivning för bättre sökmotorsynlighet</li>
                    <li>Tillagt: Komplett favicon-system (SVG + PNG i alla storlekar)</li>
                    <li>Tillagt: XML-sitemap för förbättrad sökmotorindexering</li>
                    <li>Fixat: robots.txt-format (serverade HTML)</li>
                    <li>Tillagt: Preconnect-tips för ~300ms snabbare sidladdningar</li>
                    <li>Förbättrat: JavaScript-bundle-uppdelning för bättre cachning</li>
                    <li>Fixat: Navigeringsfärgkontrast uppfyller nu tillgänglighetsstandarder</li>
                    <li>Tillagt: Content Security Policy-headers för förbättrad säkerhet</li>
                  </ul>
                </div>

                {/* v2.1.4 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.4</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">29 oktober 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Förbättrad matchvisning för mobil</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixat: Mobilmatchkort bevarar nu spelarordning (A mot B)</li>
                    <li>Vinnaren visas i fetstil oavsett vänster/höger-position</li>
                    <li>Förhindrar förvirring om hemma/borta eller utmanare/försvarare-roller</li>
                    <li>Behåller konsistens med desktop-tabellvyn</li>
                  </ul>
                </div>

                {/* v2.1.3 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.3</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">26 oktober 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Spelarens Självborttagning och GDPR-efterlevnad</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Nytt: Självborttagningsfunktion för konto på Inställningssidan</li>
                    <li>Smart borttagning: Fullständig radering om inga matcher, anonymisering om historisk data finns</li>
                    <li>GDPR-kompatibel: All personlig information tas bort vid borttagning</li>
                    <li>Matchhistorik bevarad med anonymiserade spelarnamn</li>
                    <li>Automatisk återkallelse av API-tokens vid kontoborttagning</li>
                    <li>Bekräftelsedialog med tydliga varningar</li>
                  </ul>
                </div>

                {/* v2.1.2 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.2</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">25 oktober 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Frontend-modernisering och Utvecklarupplevelse</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Uppgraderade till Vite 7 med förbättrad byggprestanda</li>
                    <li>Stora beroendeuppdateringar: recharts v3, date-fns 4, zod 4, uuid 13</li>
                    <li>Fixat: Osäker typkonvertering i diagramtooltip</li>
                    <li>Fixat: PlayerSelector oändlig loop (2000+ API-förfrågningar)</li>
                    <li>Förbättrad utvecklardokumentation (setup-codex.sh)</li>
                  </ul>
                </div>

                {/* v2.1.1 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.1</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">24 oktober 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Förbättrad Spelarsökning och Buggfixar</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixat: Spelarens "Gick med"-datum visades som "Okänt"</li>
                    <li>Fixat: MongoDB-fel vid paginering av sökresultat</li>
                    <li>Ny: Relevansskoring för spelarsökning (exakt matchning prioriteras)</li>
                    <li>Förbättrad UX: Bättre spelarväljare med klubbnamn</li>
                  </ul>
                </div>

                {/* v2.1.0 */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.1.0</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">17 oktober 2025</p>
                  <h4 className="font-semibold text-purple-700 mb-1">Sportspecifika Poängsystem</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Racket/Paddelsporter: 2-1-0 system (ITTF/BWF)</li>
                    <li>Schack: 1.0-0.5-0.0 visning (FIDE standard)</li>
                    <li>Målbaserade sporter: 3-1-0 redo (FIFA/IIHF/FIBA)</li>
                    <li>Inforutor visar aktuellt poängsystem i gruppspel</li>
                  </ul>
                </div>

                {/* v2.0.1 */}
                <div className="border-l-4 border-red-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.0.1</span>
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Säkerhetsfix</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">11 oktober 2025</p>
                  <h4 className="font-semibold text-red-700 mb-1">Matchauktorisering</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Fixade sårbarhet där oautentiserade användare såg redigera/radera-knappar</li>
                    <li>3-nivås behörighetssystem: plattformsägare → klubbadmin → spelare</li>
                    <li>Knappar nu helt dolda när användare saknar behörighet</li>
                  </ul>
                </div>

                {/* v2.0.0 */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v2.0.0</span>
                    <span className="text-xs bg-blue-100 text-info-foreground px-2 py-1 rounded">Stor release</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">11 oktober 2025</p>
                  <h4 className="font-semibold text-blue-700 mb-1">Utslagsturnering med Gruppspel</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Komplett gruppspel + knockout bracket-system</li>
                    <li>OS-stil pallvisualisering med bronsmatsch</li>
                    <li>Seedingsstrategier och byes-hantering</li>
                    <li>Mobiloptimerade matchdialoger</li>
                  </ul>
                </div>

                {/* v1.5.0 */}
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.5.0</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Ny funktion</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">5 oktober 2025</p>
                  <h4 className="font-semibold text-green-700 mb-1">Schackstöd</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Utfallsbaserad poängräkning (1-0, 0-1, 1-1)</li>
                    <li>Spårning av oavgjorda matcher i topplistan</li>
                    <li>Matchredigering för alla sporter</li>
                  </ul>
                </div>

                {/* v1.4.0 */}
                <div className="border-l-4 border-indigo-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.4.0</span>
                    <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">Förbättring</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">5 oktober 2025</p>
                  <h4 className="font-semibold text-indigo-700 mb-1">Enhetligt Ikonsystem</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Migration till Iconify (72+ ikoner)</li>
                    <li>Sportikoner på seriekort</li>
                    <li>Reducerad bundle-storlek</li>
                  </ul>
                </div>

                {/* v1.3.0 */}
                <div className="border-l-4 border-orange-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.3.0</span>
                    <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">Ny funktion</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">4 oktober 2025</p>
                  <h4 className="font-semibold text-orange-700 mb-1">Stege-format</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Klassisk och Aggressiv stege-variant</li>
                    <li>Utmaningsbaserat tävlingsformat</li>
                    <li>Dubbla rankingssystem (ELO + Stege)</li>
                  </ul>
                </div>

                {/* v1.2.0 */}
                <div className="border-l-4 border-pink-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.2.0</span>
                    <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded">Ny funktion</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">4 oktober 2025</p>
                  <h4 className="font-semibold text-pink-700 mb-1">Multisport-expansion</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Badminton, Squash, Padel, Pickleball</li>
                    <li>Utökat set-intervall: 3, 5, eller 7</li>
                  </ul>
                </div>

                {/* v1.1.0 */}
                <div className="border-l-4 border-teal-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.1.0</span>
                    <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">Ny funktion</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">3 oktober 2025</p>
                  <h4 className="font-semibold text-teal-700 mb-1">Tennisstöd</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Tennis matchrapportering med ELO</li>
                    <li>Sportikoner (CircleDot för pingis, Circle för tennis)</li>
                  </ul>
                </div>

                {/* v1.0.x patches */}
                <div className="border-l-4 border-muted-foreground pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.0.1 - v1.0.3</span>
                    <span className="text-xs bg-muted text-foreground px-2 py-1 rounded">Buggfixar</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">3 oktober 2025</p>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>v1.0.3: Fix för spelarsökfilter</li>
                    <li>v1.0.2: gRPC konfigurationsnormalisering</li>
                    <li>v1.0.1: Borttagning av oanvänd kod</li>
                  </ul>
                </div>

                {/* v1.0.0 */}
                <div className="border-l-4 border-foreground pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-lg">v1.0.0</span>
                    <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded">Första versionen</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">2 oktober 2025</p>
                  <h4 className="font-semibold text-muted-foreground mb-1">Initial Release</h4>
                  <ul className="text-sm space-y-1 text-muted-foreground list-disc list-inside">
                    <li>Komplett bordtennisturneringshantering</li>
                    <li>Go backend med gRPC/REST + MongoDB</li>
                    <li>React TypeScript frontend</li>
                    <li>ELO-betygssystem och topplista</li>
                    <li>Magic link-autentisering</li>
                    <li>Svensk och engelsk översättning</li>
                  </ul>
                </div>

              </div>
              
              <div className="mt-4 p-3 bg-muted rounded text-sm text-muted-foreground">
                📖 Fullständig ändringslogg finns tillgänglig i projektets CHANGELOG_SV.md
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Support */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon icon={ICONS.info} className="text-info" />
            Hjälp och support
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground">
            Om du hittar buggar eller har funktionsförslag, kontakta din
            klubbadministratör eller mejla <a href="mailto:goran@goencoder.se" className="text-info hover:underline">goran@goencoder.se</a>.
          </p>
          <div className="bg-info-muted border border-info/30 rounded p-4 text-info-muted-foreground">
            <p className="text-sm">
              <strong>Version:</strong> {APP_VERSION}
            </p>
            <p className="text-sm mt-1">
              <strong>Support:</strong> <a href="mailto:goran@goencoder.se" className="text-info hover:underline">goran@goencoder.se</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
